/**
 * Olimpo Zoom
 * Swipe navigation, pinch-to-zoom, double-tap and keyboard nav for the product media modal.
 * Active on all viewports.
 */
(() => {
  'use strict';

  const SWIPE_THRESH = 50;
  const MAX_SCALE = 3.5;
  const DBL_SCALE = 2.5;

  class MobileZoom {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector('.product-media-modal__dialog');
      this.content = modal.querySelector('.product-media-modal__content');
      this.items = [...this.content.querySelectorAll(':scope > [data-media-id]')];
      this.idx = Math.max(0, this.items.findIndex(el => el.classList.contains('active')));

      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this._lr = null;
      this._t = {};
      this._lastTap = 0;
      this._snapTimer = null;

      this._hTS = this._onTS.bind(this);
      this._hTM = this._onTM.bind(this);
      this._hTE = this._onTE.bind(this);
      this._hResize = () => {
        if (this.scale > 1) this._resetZoom(false);
        this._layout();
      };

      this._ui();
      this._listen();
      this._layout();
      this._sync();
    }

    /* ── Helpers ── */

    _img() {
      const el = this.items[this.idx];
      return el?.tagName === 'IMG' ? el : el?.querySelector('img');
    }

    _isImg() {
      return this.items[this.idx]?.tagName === 'IMG';
    }

    _dist(e) {
      return Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }

    _center(e) {
      return {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }

    /* ── UI: navigation arrows + counter ── */

    _ui() {
      if (this.items.length < 2) return;

      const mkBtn = (cls, label, points) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `olimpo-modal-nav ${cls}`;
        b.setAttribute('aria-label', label);
        b.innerHTML =
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" ' +
          'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" ' +
          'stroke-linejoin="round"><polyline points="' + points + '"/></svg>';
        return b;
      };

      this._prev = mkBtn('olimpo-modal-nav--prev', 'Anterior', '15 18 9 12 15 6');
      this._next = mkBtn('olimpo-modal-nav--next', 'Próxima', '9 6 15 12 9 18');
      this._ctr = document.createElement('div');
      this._ctr.className = 'olimpo-modal-counter';

      this.dialog.append(this._prev, this._next, this._ctr);
      this._prev.addEventListener('click', () => this._nav(-1));
      this._next.addEventListener('click', () => this._nav(1));

      [this._prev, this._next, this._ctr].forEach(el => {
        el.addEventListener('pointerup', e => e.stopPropagation());
      });
    }

    /* ── Events ── */

    _listen() {
      this.content.addEventListener('touchstart', this._hTS, { passive: false });
      this.content.addEventListener('touchmove', this._hTM, { passive: false });
      this.content.addEventListener('touchend', this._hTE, { passive: true });

      this._hMD = this._onMD.bind(this);
      this._hMM = this._onMM.bind(this);
      this._hMU = this._onMU.bind(this);
      this._hDblClick = this._onDblClick.bind(this);
      this.content.addEventListener('mousedown', this._hMD);
      window.addEventListener('mousemove', this._hMM);
      window.addEventListener('mouseup', this._hMU);
      this.content.addEventListener('dblclick', this._hDblClick);

      this._hKey = this._onKey.bind(this);
      document.addEventListener('keydown', this._hKey);
      window.addEventListener('resize', this._hResize);
    }

    _onKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this._nav(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this._nav(1); }
    }

    /* ── Mouse drag (desktop swipe) ── */

    _onMD(e) {
      if (e.button !== 0) return;
      if (e.target.closest('.olimpo-modal-nav, .olimpo-modal-counter, .product-media-modal__toggle')) return;
      e.preventDefault();
      if (this.scale > 1.05 && this._isImg()) {
        this._mouse = { mode: 'pan', x0: e.clientX, y0: e.clientY, tx0: this.tx, ty0: this.ty };
        this.content.style.cursor = 'grabbing';
      } else {
        this._mouse = { mode: 'swipe', x0: e.clientX, t0: Date.now(), dx: 0 };
      }
    }

    _onMM(e) {
      if (!this._mouse) return;
      if (this._mouse.mode === 'pan') {
        this.tx = this._mouse.tx0 + e.clientX - this._mouse.x0;
        this.ty = this._mouse.ty0 + e.clientY - this._mouse.y0;
        this._clamp();
        this._zoom(false);
        return;
      }
      this._mouse.dx = e.clientX - this._mouse.x0;
      if (Math.abs(this._mouse.dx) > 10 && this._isImg()) {
        const img = this._img();
        if (img) {
          img.style.transition = 'none';
          img.style.transform = `translateX(${this._mouse.dx * 0.35}px)`;
          img.style.opacity = String(1 - Math.min(Math.abs(this._mouse.dx) / window.innerWidth, 0.3));
        }
      }
    }

    _onMU(e) {
      if (!this._mouse) return;
      if (this._mouse.mode === 'pan') {
        this.content.style.cursor = this.scale > 1.05 ? 'grab' : '';
        this._mouse = null;
        return;
      }
      const { dx, x0, t0 } = this._mouse;
      const realDx = e.clientX - x0;
      const dt = Date.now() - t0;
      this._mouse = null;

      const shouldNav =
        (Math.abs(realDx) > SWIPE_THRESH || (dt > 0 && Math.abs(realDx) / dt > 0.4)) &&
        Math.abs(dx) > 15;

      if (shouldNav) {
        this._clearImgStyles();
        this._nav(realDx > 0 ? -1 : 1);
      } else {
        this._snapBack();
      }
    }

    /* ── Touch start ── */

    _onTS(e) {
      const isImg = this._isImg();

      if (e.touches.length === 2 && isImg) {
        e.preventDefault();
        const c = this._center(e);
        const img = this._img();
        const r = img?.getBoundingClientRect();
        this._t = {
          m: 'pinch',
          d0: this._dist(e),
          s0: this.scale,
          tx0: this.tx,
          ty0: this.ty,
          ox: r ? c.x - r.left + this.tx : 0,
          oy: r ? c.y - r.top + this.ty : 0,
        };
      } else if (e.touches.length === 1) {
        if (this.scale > 1.05 && isImg) {
          e.preventDefault();
          this._t = {
            m: 'pan',
            x0: e.touches[0].clientX,
            y0: e.touches[0].clientY,
            t0: Date.now(),
            tx0: this.tx,
            ty0: this.ty,
          };
        } else {
          this._t = {
            m: 'swipe',
            x0: e.touches[0].clientX,
            y0: e.touches[0].clientY,
            t0: Date.now(),
            dx: 0,
          };
        }
      }
    }

    /* ── Touch move ── */

    _onTM(e) {
      const t = this._t;
      if (!t.m) return;

      if (t.m === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const ns = Math.min(Math.max(t.s0 * this._dist(e) / t.d0, 1), MAX_SCALE);
        const r = ns / t.s0;
        this.tx = t.ox - (t.ox - t.tx0) * r;
        this.ty = t.oy - (t.oy - t.ty0) * r;
        this.scale = ns;
        this._clamp();
        this._zoom(false);
      } else if (t.m === 'pan') {
        e.preventDefault();
        this.tx = t.tx0 + e.touches[0].clientX - t.x0;
        this.ty = t.ty0 + e.touches[0].clientY - t.y0;
        this._clamp();
        this._zoom(false);
      } else if (t.m === 'swipe') {
        const dy = e.touches[0].clientY - t.y0;
        t.dx = e.touches[0].clientX - t.x0;

        if (Math.abs(t.dx) > Math.abs(dy) && Math.abs(t.dx) > 10) {
          e.preventDefault();
          if (this._isImg()) {
            const img = this._img();
            if (img) {
              img.style.transition = 'none';
              img.style.transform = `translateX(${t.dx * 0.35}px)`;
              img.style.opacity = String(1 - Math.min(Math.abs(t.dx) / window.innerWidth, 0.3));
            }
          }
        }
      }
    }

    /* ── Touch end ── */

    _onTE(e) {
      const t = this._t;
      if (!t.m) { this._t = {}; return; }

      if (t.m === 'pinch') {
        if (this.scale <= 1.05) this._resetZoom(true);
      } else if (t.m === 'pan') {
        const dx = e.changedTouches[0].clientX - t.x0;
        const dy = e.changedTouches[0].clientY - t.y0;
        const dt = Date.now() - (t.t0 || 0);
        const wasTap = Math.abs(dx) < 10 && Math.abs(dy) < 10 && dt < 300;

        if (wasTap) {
          const now = Date.now();
          if (this._lastTap && now - this._lastTap < 400) {
            this._lastTap = 0;
            this._dblTap(e.changedTouches[0]);
          } else {
            this._lastTap = now;
          }
        }
      } else if (t.m === 'swipe') {
        const dx = e.changedTouches[0].clientX - t.x0;
        const dt = Date.now() - t.t0;
        const shouldNav =
          (Math.abs(dx) > SWIPE_THRESH || (dt > 0 && Math.abs(dx) / dt > 0.4)) &&
          Math.abs(t.dx) > 15;

        if (shouldNav) {
          this._clearImgStyles();
          this._nav(dx > 0 ? -1 : 1);
        } else {
          this._snapBack();
        }

        if (Math.abs(t.dx) < 10 && dt < 300) {
          const now = Date.now();
          if (this._lastTap && now - this._lastTap < 400) {
            this._lastTap = 0;
            this._dblTap(e.changedTouches[0]);
          } else {
            this._lastTap = now;
          }
        }
      }

      this._t = {};
    }

    /* ── Swipe feedback helpers ── */

    _clearImgStyles() {
      const img = this._img();
      if (img) {
        img.style.transform = '';
        img.style.opacity = '';
        img.style.transition = '';
      }
    }

    _snapBack() {
      const img = this._img();
      if (!img) return;
      img.style.transition = 'transform .25s ease, opacity .25s ease';
      img.style.transform = '';
      img.style.opacity = '';
      clearTimeout(this._snapTimer);
      this._snapTimer = setTimeout(() => { img.style.transition = ''; }, 280);
    }

    /* ── Zoom transform ── */

    _zoom(animate) {
      const img = this._img();
      if (!img) return;
      img.style.transformOrigin = '0 0';
      img.style.transition = animate ? 'transform .3s cubic-bezier(.2,.8,.3,1)' : 'none';
      img.style.transform = `translate(${this.tx}px,${this.ty}px) scale(${this.scale})`;

      const z = this.scale > 1.05;
      this._prev?.classList.toggle('olimpo-modal-nav--hidden', z);
      this._next?.classList.toggle('olimpo-modal-nav--hidden', z);
      this._ctr?.classList.toggle('olimpo-modal-counter--hidden', z);
    }

    _resetZoom(animate) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      const img = this._img();
      if (img) {
        img.style.transformOrigin = '';
        img.style.transition = animate ? 'transform .3s cubic-bezier(.2,.8,.3,1)' : '';
        img.style.transform = '';
        if (animate) setTimeout(() => { img.style.transition = ''; }, 350);
      }
      this._prev?.classList.remove('olimpo-modal-nav--hidden');
      this._next?.classList.remove('olimpo-modal-nav--hidden');
      this._ctr?.classList.remove('olimpo-modal-counter--hidden');
      this._updateCursor();
    }

    /* ── Constrain pan to image bounds ── */

    _clamp() {
      if (this.scale <= 1 || !this._lr) return;
      const { left: lx, top: ly, w, h } = this._lr;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const sw = w * this.scale;
      const sh = h * this.scale;

      this.tx = sw > vw
        ? Math.max(vw - lx - sw, Math.min(-lx, this.tx))
        : (vw - sw) / 2 - lx;

      this.ty = sh > vh
        ? Math.max(vh - ly - sh, Math.min(-ly, this.ty))
        : (vh - sh) / 2 - ly;
    }

    /* ── Double-tap: toggle zoom at tap point ── */

    _dblTap(touch) {
      if (this.scale > 1.05) {
        this._resetZoom(true);
        return;
      }
      if (!this._lr || !this._isImg()) return;
      this.tx = (touch.clientX - this._lr.left) * (1 - DBL_SCALE);
      this.ty = (touch.clientY - this._lr.top) * (1 - DBL_SCALE);
      this.scale = DBL_SCALE;
      this._clamp();
      this._zoom(true);
    }

    /* ── Desktop: double-click zoom toggle ── */

    _onDblClick(e) {
      if (e.target.closest('.olimpo-modal-nav, .olimpo-modal-counter, .product-media-modal__toggle')) return;
      if (!this._isImg()) return;
      e.preventDefault();
      clearTimeout(this._snapTimer);
      if (this.scale > 1.05) {
        this._resetZoom(true);
      } else {
        if (!this._lr) return;
        this.tx = (e.clientX - this._lr.left) * (1 - DBL_SCALE);
        this.ty = (e.clientY - this._lr.top) * (1 - DBL_SCALE);
        this.scale = DBL_SCALE;
        this._clamp();
        this._zoom(true);
      }
      this._updateCursor();
    }

    _updateCursor() {
      if (!this.content) return;
      this.content.style.cursor = this.scale > 1.05 ? 'grab' : '';
    }

    /* ── Layout rect capture (when scale=1, tx=ty=0) ── */

    _layout() {
      const img = this._img();
      if (!img) return;
      const r = img.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        this._lr = { left: r.left, top: r.top, w: r.width, h: r.height };
      } else {
        img.addEventListener('load', () => this._layout(), { once: true });
      }
    }

    /* ── Navigation ── */

    _nav(dir) {
      const ni = this.idx + dir;
      if (ni < 0 || ni >= this.items.length) return;
      this._resetZoom(false);
      this._clearImgStyles();
      this.items[this.idx].classList.remove('active');
      this.idx = ni;
      this.items[this.idx].classList.add('active');
      requestAnimationFrame(() => { this._layout(); this._sync(); });
    }

    _sync() {
      if (this._ctr) this._ctr.textContent = `${this.idx + 1} / ${this.items.length}`;
      this._prev?.classList.toggle('olimpo-modal-nav--disabled', this.idx === 0);
      this._next?.classList.toggle('olimpo-modal-nav--disabled', this.idx === this.items.length - 1);
    }

    /* ── Cleanup ── */

    destroy() {
      this.content.removeEventListener('touchstart', this._hTS);
      this.content.removeEventListener('touchmove', this._hTM);
      this.content.removeEventListener('touchend', this._hTE);
      this.content.removeEventListener('mousedown', this._hMD);
      window.removeEventListener('mousemove', this._hMM);
      window.removeEventListener('mouseup', this._hMU);
      this.content.removeEventListener('dblclick', this._hDblClick);
      document.removeEventListener('keydown', this._hKey);
      window.removeEventListener('resize', this._hResize);
      clearTimeout(this._snapTimer);
      this._resetZoom(false);
      this.content.style.cursor = '';
      this._prev?.remove();
      this._next?.remove();
      this._ctr?.remove();
    }
  }

  /* ── Bootstrap: observe modal open/close ── */

  let ctrl = null;

  const observe = (modal) => {
    new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.attributeName !== 'open') continue;
        if (modal.hasAttribute('open')) {
          requestAnimationFrame(() => {
            ctrl?.destroy();
            ctrl = new MobileZoom(modal);
          });
        } else if (!modal.hasAttribute('open') && ctrl) {
          ctrl.destroy();
          ctrl = null;
        }
      }
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  };

  document.querySelectorAll('product-modal').forEach(observe);
})();
